// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { db, auth, paths } from "../firebase/config";
import {
  signInAnonymously,
  onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { limparCPF } from "../utils/helpers";

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const ADMIN_SEED_CPF = "11122233344";

// 30 minutos em milissegundos para timeout de inatividade
const SESSION_TIMEOUT_MS = 7200000; 

// --- NOVA TRAVA DE VERSÃO ---
// MUDAMOS PARA 2.2 PARA FORÇAR A LIMPEZA DO PWA DE QUEM JÁ BAIXOU A 2.0
const APP_VERSION = "v2.2-otimizada";

export const logAuditoria = async (user, acao, payload = {}) => {
  try {
    await addDoc(collection(db, paths.logs), {
      usuario_id: user?.id || "sistema",
      usuario_nome: user?.nome || "Sistema",
      acao,
      payload: JSON.stringify(payload),
      criado_em: serverTimestamp(),
    });
  } catch (e) {
    console.error("Erro ao registrar log:", e);
  }
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isAdminCreated, setIsAdminCreated] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const timeoutRef = useRef(null);

  // --- EFEITO DA TRAVA DE VERSÃO (AGORA COM LIMPEZA DE PWA/SERVICE WORKERS) ---
  useEffect(() => {
    const currentVersion = localStorage.getItem("siaap_app_version");
    
    if (currentVersion !== APP_VERSION) {
      console.log("Nova versão detetada! Limpando cache do aplicativo (PWA)...");
      
      auth.signOut().then(() => {
        
        // 1. Limpa o Cache Storage do Navegador (PWA Cache)
        if ('caches' in window) {
            caches.keys().then((names) => {
                for (let name of names) {
                    caches.delete(name);
                }
            });
        }

        // 2. Mata o Service Worker (O "vírus do bem" que segura a versão antiga)
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                for (let registration of registrations) {
                    registration.unregister();
                }
            }).then(() => {
                // 3. Salva a nova versão e recarrega
                localStorage.setItem("siaap_app_version", APP_VERSION);
                window.location.reload(true);
            });
        } else {
            // Se o navegador não suportar Service Worker (PC antigo, etc), só recarrega
            localStorage.setItem("siaap_app_version", APP_VERSION);
            window.location.reload(true);
        }

      }).catch(err => {
          console.error("Erro ao deslogar versão antiga:", err);
          localStorage.setItem("siaap_app_version", APP_VERSION);
          window.location.reload(true);
      });
    }
  }, []);

  const createAdminUser = async () => {
    try {
      console.log("A criar Admin Mestre...");
      const adminRef = doc(collection(db, paths.users));
      const newAdminProfile = {
        id: adminRef.id,
        cpf: ADMIN_SEED_CPF,
        nome: "Maj PM EO (Admin)",
        nivel: "admin",
        pelotao_id: null,
        senha: ADMIN_SEED_CPF,
      };

      await setDoc(adminRef, newAdminProfile);
      await logAuditoria(newAdminProfile, "Criação automática do Admin Mestre");

      setUserProfile(newAdminProfile);
      setIsAdminCreated(true);

      console.log("Admin Mestre criado e logado.");
      return true;
    } catch (error) {
      console.error("Erro ao criar Admin Mestre:", error);
      return false;
    }
  };

  const logoutApp = useCallback((showWarning = false) => {
    setUserProfile(null);
    setIsAdminCreated(null);
    setLoadingAuth(true);
    
    if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
    }

    if (showWarning) {
      localStorage.setItem('sessionTimeoutWarning', 'true');
    }

    auth.signOut();
  }, []);

  const resetSessionTimeout = useCallback(() => {
    if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setSessionExpired(true);
    }, SESSION_TIMEOUT_MS);
  }, []);

  // Efeito para configurar listeners de atividade de forma otimizada
  useEffect(() => {
    if (!userProfile) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setSessionExpired(false);
      return;
    }

    const events = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    let throttleTimer = null;
    const handleActivity = () => {
        if (throttleTimer) return;
        throttleTimer = setTimeout(() => {
            resetSessionTimeout();
            throttleTimer = null;
        }, 1000); 
    };

    resetSessionTimeout(); 

    events.forEach(event => window.addEventListener(event, handleActivity));

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (throttleTimer) clearTimeout(throttleTimer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [userProfile, resetSessionTimeout]);

  // Efeito de autenticação isolado 
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);
      } else {
        try {
          if (
            typeof __initial_auth_token !== "undefined" &&
            __initial_auth_token
          ) {
            await signInWithCustomToken(auth, __initial_auth_token);
          } else {
            await signInAnonymously(auth);
          }
        } catch (error) {
          console.error("Falha no login automático:", error);
          setLoadingAuth(false);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Efeito separado para gerenciar a lógica de carregamento e bypass do Admin
  useEffect(() => {
      if (firebaseUser) {
          if (isAdminCreated === null && !userProfile) {
              setIsAdminCreated(true);
          }
          if (!userProfile) {
              setLoadingAuth(false);
          }
      }
  }, [firebaseUser, isAdminCreated, userProfile]);

  // Efeito isolado para a expiração de sessão
  useEffect(() => {
    if (sessionExpired && userProfile) {
      logoutApp(true);
      setSessionExpired(false);
    }
  }, [sessionExpired, userProfile, logoutApp]);

  const loginApp = async (cpf, senha) => {
    const cpfLimpo = limparCPF(cpf);
    if (cpfLimpo.length !== 11) {
      throw new Error("CPF deve conter 11 dígitos.");
    }
    if (!senha) {
      throw new Error("Senha é obrigatória.");
    }

    const q = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
    try {
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error("CPF ou senha incorreta.");
      }

      const userDoc = snapshot.docs[0];
      const profileData = { id: userDoc.id, ...userDoc.data() };

      if (profileData.senha !== senha) {
        await logAuditoria(
          profileData,
          "Tentativa de login (Senha inválida)"
        );
        throw new Error("CPF ou senha incorreta.");
      }

      setUserProfile(profileData);
      setLoadingAuth(false);

      await logAuditoria(profileData, "Login efetuado");
      resetSessionTimeout(); 

      return profileData;
    } catch (error) {
      console.error("Erro no loginApp:", error);
      throw error;
    }
  };

  const value = {
    firebaseUser,
    userProfile,
    loadingAuth,
    isAdminCreated,
    createAdminUser,
    loginApp,
    logoutApp,
    isAdmin: userProfile?.nivel === "admin",
    isEscalante: userProfile?.nivel === "escalante",
    isCadete: userProfile?.nivel === "cadete",
    isGerenciador: userProfile?.nivel === "gerenciador",
    isCadeteAcademico: userProfile?.nivel === "cadete_academico",
    isSoe: userProfile?.nivel === "soe",
    isCmtPelotao: userProfile?.nivel === "cmt_pelotao",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};